import React from 'react';

/**
 * Whether the model named the right thing, on the row where it named it.
 *
 * The run header reports one identification percentage. This is the same score
 * per answer, so the misses can be found and read rather than inferred from an
 * average — and the two things being compared are printed underneath, because
 * a verdict is only actionable next to the claim it was graded against.
 *
 * Four states, kept distinct on purpose:
 *   scored + graded   brand ✓ / ✗
 *   scored, no brand  the manifest names none, so brand is not graded ("Brand -")
 *   not scored        a multi-item pallet or a blank line; muted, never a miss
 *   unavailable       the run's dataset has no manifest at all, so nothing was
 *                     ever graded — no badge, rather than a "not scored" chip
 *                     on every row blaming a line nobody read
 */
const IdentificationBadge = ({ identification, formatTooltip, showExpectedName = false }) => {
  if (!identification || identification.available === false) return null;
  const tooltip = typeof formatTooltip === 'function' ? formatTooltip(identification) : undefined;

  if (!identification.scored) {
    return (
      <span
        className="inline-flex rounded bg-surface-2 px-1 py-0.5 text-[9px] font-semibold text-muted"
        title={tooltip}
      >
        Not scored
      </span>
    );
  }

  const { brandCorrect, nameF1, modelBrand, expectedBrand, expectedName } = identification;
  const brandClass =
    brandCorrect === true
      ? 'bg-positive/10 text-positive'
      : brandCorrect === false
        ? 'bg-danger/10 text-danger'
        : 'bg-surface-2 text-muted';
  const brandLabel = brandCorrect === true ? '✓' : brandCorrect === false ? '✗' : '-';
  const nameClass =
    nameF1 === null
      ? 'bg-surface-2 text-muted'
      : nameF1 >= 0.75
        ? 'bg-positive/10 text-positive'
        : nameF1 >= 0.4
          ? 'bg-warn/10 text-warn'
          : 'bg-danger/10 text-danger';

  return (
    <div className="space-y-0.5" title={tooltip}>
      <span className="inline-flex flex-wrap items-center gap-1">
        <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold ${brandClass}`}>
          Brand {brandLabel}
        </span>
        <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold ${nameClass}`}>
          F1 {nameF1 === null ? '-' : nameF1.toFixed(2)}
        </span>
      </span>

      {/* The brand is per item — each answer claims its own — so it prints on
          every row. An empty model brand is a run recorded before the rig kept
          the field, not a model that declined to answer. */}
      <div className="text-[9px] leading-snug text-muted">
        <span className="font-semibold">Brand:</span>{' '}
        {modelBrand || <span className="italic">not recorded</span>}
        {' · manifest: '}
        {expectedBrand || <span className="italic">none named</span>}
      </div>

      {/* The manifest line is lot-level truth — the same sentence for every
          item in the lot — so it prints once, on the lot's first row. */}
      {showExpectedName && expectedName ? (
        <div className="text-[9px] leading-snug text-muted">
          <span className="font-semibold">Manifest:</span> {expectedName}
        </div>
      ) : null}
    </div>
  );
};

export default IdentificationBadge;
